import { format, parseISO } from "date-fns"

import Icon from "@/components/Icon"
import { type IncentiveFormValues, hasAppComms } from "@/components/incentives/incentiveFormSchema"

import { ukToday } from "../../../shared/shift-timing.ts"

const DAY_MS = 24 * 60 * 60 * 1000

// Both are calendar days with no time of day, so the difference in UTC is a whole number of days.
const endsIn = (endDate: string) => {
  const days = Math.round((Date.parse(endDate) - Date.parse(ukToday())) / DAY_MS)
  if (days <= 0) return "Ends today"
  return days === 1 ? "Ends in 1 day" : `Ends in ${days} days`
}

const Chip = ({ children, className }: { children: React.ReactNode; className: string }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}
  >
    {children}
  </span>
)

// A guide to how the app reads, not the app's own components.
export default function AppCommsPreview({ values }: { values: IncentiveFormValues }) {
  const title = values.app_comms.title.trim()
  const text = values.app_comms.text.trim()
  const terms = values.app_comms.terms.trim()
  const endDate = values.end_date

  return (
    <div className="space-y-4 rounded-lg border border-palette-jungleGreen200 bg-white p-4">
      <span className="flex items-center gap-1.5 text-xs font-medium text-palette-jungleGreen600">
        <Icon name="infoCircle" className="h-3.5 w-3.5 text-current" />
        App preview
      </span>
      <p className="text-xs leading-relaxed text-palette-jungleGreen600">
        Note: A driver may see other incentives alongside this, depending on their profile and which
        other incentives are live.
      </p>
      {!hasAppComms(values.app_comms) ? (
        <p className="text-sm text-palette-jungleGreen600">
          Add a title and text to see how it reads in the app.
        </p>
      ) : (
        <>
          <section aria-label="Shifts page preview" className="space-y-1.5">
            <p className="text-xs font-medium text-palette-jungleGreen600">Shifts page</p>
            <div className="max-w-sm rounded-xl bg-palette-jungleGreen000 p-3">
              <div className="space-y-3 rounded-lg bg-palette-jungleGreen900 p-4 text-white">
                <p className="flex items-center gap-2 font-semibold">
                  <Icon name="lightning" className="h-4 w-4 text-palette-honeycomb500" />
                  Incentives
                </p>
                <div className="flex items-center justify-between gap-2">
                  <div className="space-y-1.5">
                    <p className="text-sm">{title}</p>
                    <div className="flex flex-wrap gap-1.5">
                      <Chip className="bg-palette-honeycomb500 text-palette-jungleGreen900">
                        New
                      </Chip>
                      {endDate && (
                        <Chip className="bg-palette-jungleGreen700 text-white">
                          <Icon name="hourglass" className="h-3 w-3 text-current" />
                          {endsIn(endDate)}
                        </Chip>
                      )}
                    </div>
                  </div>
                  <Icon name="caretRight" className="h-4 w-4 shrink-0 text-current" />
                </div>
              </div>
            </div>
          </section>

          <section aria-label="Details preview" className="space-y-1.5">
            <p className="text-xs font-medium text-palette-jungleGreen600">
              Tapped on the shifts page
            </p>
            <div className="max-w-sm rounded-xl bg-palette-jungleGreen000 p-3">
              <div className="rounded-lg bg-white text-palette-jungleGreen900 shadow-sm">
                <div className="flex items-center justify-between gap-2 border-b border-palette-jungleGreen100 px-4 py-3">
                  <p className="font-semibold">{title}</p>
                  <Icon name="close" className="h-4 w-4 shrink-0 text-current" />
                </div>
                <div className="space-y-3 px-4 py-3 text-sm">
                  {endDate && (
                    <p className="font-semibold">
                      {endsIn(endDate)} · {format(parseISO(endDate), "do MMM")}
                    </p>
                  )}
                  <p>{text}</p>
                  {terms && (
                    <div className="space-y-1 text-xs">
                      <p className="font-semibold">Terms</p>
                      <p className="whitespace-pre-line">{terms}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section aria-label="Homepage banner preview" className="space-y-1.5">
            <p className="text-xs font-medium text-palette-jungleGreen600">Homepage banner</p>
            {values.app_comms.show_homepage_banner ? (
              <div className="max-w-sm rounded-xl bg-palette-jungleGreen000 p-3">
                <div className="flex items-center gap-3 rounded-lg bg-palette-jungleGreen900 p-4 text-white">
                  <Icon name="lightning" className="h-5 w-5 shrink-0 text-palette-honeycomb500" />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    {endDate && (
                      <p className="text-xs text-palette-honeycomb500">
                        Ends {format(parseISO(endDate), "EEE d MMM")}
                      </p>
                    )}
                    <p className="font-semibold">{title}</p>
                    <p className="text-sm">{text}</p>
                  </div>
                  <Icon name="caretRight" className="h-4 w-4 shrink-0 text-current" />
                </div>
              </div>
            ) : (
              <p className="text-sm text-palette-jungleGreen600">
                Off. Turn on "Show as homepage banner" to show it there.
              </p>
            )}
          </section>
        </>
      )}
    </div>
  )
}
